
import { Component, Ref, Vue, Watch } from 'vue-property-decorator';

// Utils
import { hasPermission } from '@/utils/Permissions';
import ErrorService from '@/utils/ErrorService';

// Api
import { getAllProjects } from '@/utils/api/Projects';
import { getSection, updateSection } from '@/utils/api/Sections';
// Components
import VAdministrationMain from '@/components/Administration/VAdministrationMain.vue';
import VAdministrationHeader from '@/components/Administration/VAdministrationHeader.vue';
import VPageTitle from '@/components/Administration/VPageTitle.vue';
import VAdministrationWidget from '@/components/Administration/VAdministrationWidget.vue';

import VAdministrationFloorsTable from '@/components/Administration/VAdministrationFloorsTable.vue';

import { ValidationObserver, ValidationProvider } from 'vee-validate';
import VAdministrationForm from '@/components/Administration/VAdministrationForm.vue';

import VInput from '@/components/VInput.vue';
import VButton from '@/components/VButton/VButton.vue';
import VFormField from '@/components/VFormField.vue';
import VSelect from '@/components/VSelect.vue';
import VBreadcrumbs from '@/components/VBreadcrumbs.vue';
import VPoppedAlert from '@/components/VPoppedAlert.vue';

// Enums
import PermissionsEnum from '@/ship/Enums/PermissionsEnum';

// Interfaces
type FilteredProject = Pick<Project, 'id' | 'project'>;
import Section from '@/ship/Models/Section';
import Project from '@/ship/Models/Project';
import { AxiosError } from 'axios';

@Component({
    components: {
        VAdministrationMain,
        VAdministrationHeader,
        VPageTitle,
        VAdministrationWidget,

        VAdministrationFloorsTable,

        ValidationObserver,
        ValidationProvider,
        VAdministrationForm,

        VInput,
        VButton,
        VFormField,
        VSelect,
        VBreadcrumbs,
        VPoppedAlert,
    },
})
export default class SectionEditPage extends Vue {
    @Ref() poppedAlert!: VPoppedAlert;

    public isLoading: boolean = false;
    public projects: FilteredProject[] = [];
    public model: { name: string; projectId: number | null } = {
        name: '',
        projectId: null,
    };

    get sectionId(): number {
        return +this.$route.params.id;
    }
    get currentProjectId(): number | null {
        return this.model.projectId ?? null;
    }

    get hasCreateFloorAccess() {
        return hasPermission(PermissionsEnum.FloorsCreate);
    }

    @Watch('sectionId')
    public async actualizeSection() {
        const response = await getSection<Pick<Section, 'name' | 'projectId'>>(this.sectionId, {
            filter: 'name;project_id',
        }).catch((error) => {
            if ((error as AxiosError).response?.status === 404) this.$router.push({ name: 'error.404' });
            else ErrorService.handleApiError(error);
        });

        if (response) this.model = response.data.data;
    }

    public async actualizeProjects() {
        const response = await getAllProjects<FilteredProject>({
            orderBy: 'project',
            filter: 'id;project',
            limit: 0,
        }).catch(ErrorService.handleApiError);
        if (response) this.projects = response.data.data;
    }

    public async handleSubmit() {
        this.isLoading = true;
        const response = await updateSection(this.sectionId, this.model.name, this.model.projectId).catch(
            ErrorService.handleApiError,
        );
        if (response) this.showSuccess();
        this.isLoading = false;
    }

    public showSuccess() {
        this.poppedAlert?.show(this.$t('dataSavedSuccessfully'));
    }

    public async created() {
        this.isLoading = true;
        await this.actualizeSection();
        await this.actualizeProjects();
        this.isLoading = false;
    }
}
